// Importaciones necesarias
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { fetchAdjudicacionById, updateAdjudicacion, deleteAdjudicacion } from '../api/adjudicacionesApi';
import { useAuth } from '../security/AuthLogin';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faLink } from '@fortawesome/free-solid-svg-icons'; // Se ha eliminado faHeart
import '../css/concurso.css';

// Importamos componentes de Ant Design y Recharts
import { Progress } from 'antd';
import 'antd/dist/reset.css'; // Estilos para Ant Design v5
import { PieChart, Pie, Cell } from 'recharts';

const Adjudicacion: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const isEditable = userRole !== 'cliente'; // Solo admins y técnicos pueden editar

  const [adjudicacion, setAdjudicacion] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAdjudicacion = async () => {
      try {
        setError(null);
        const data = await fetchAdjudicacionById(id!);
        setAdjudicacion(data);
      } catch (err) {
        setError('Error al cargar los datos de la adjudicación');
      }
    };

    if (id) {
      loadAdjudicacion();
    }
  }, [id]);

  const [pdfViewer, setPdfViewer] = useState({ isOpen: false, link: '', name: '' });

  const handleViewPDF = (link: string, name: string) => {
    setPdfViewer({ isOpen: true, link, name });
  };

  const closePDFViewer = () => {
    setPdfViewer({ isOpen: false, link: '', name: '' });
  };

  const handleEdit = () => {
    if (isEditable) {
      setEditing(true);
    }
  };

  const handleSave = async () => {
    try {
      const updatedAdjudicacion = {
        ...adjudicacion,
        cpv: adjudicacion.cpv ? adjudicacion.cpv.filter((cpv: string) => cpv.trim() !== '') : [],
        documentos: adjudicacion.documentos
          ? adjudicacion.documentos.filter(
              (doc: any) => doc.link.trim() !== '' && doc.nombre.trim() !== ''
            )
          : []
      };
      await updateAdjudicacion(id!, updatedAdjudicacion);
      setEditing(false);
      setAdjudicacion(updatedAdjudicacion);
    } catch (err) {
      setError('Error al guardar los cambios en la adjudicación');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAdjudicacion(id!);
      navigate('/adjudicaciones'); // Redirige al listado de adjudicaciones
    } catch (err) {
      setError('Error al eliminar la adjudicación');
    }
  };

  const handleAddCPV = () => {
    if (isEditable) {
      setAdjudicacion({ ...adjudicacion, cpv: ['', ...(adjudicacion.cpv || [])] });
    }
  };

  const handleRemoveCPV = (index: number) => {
    if (isEditable) {
      const updatedCPV = [...adjudicacion.cpv];
      updatedCPV.splice(index, 1);
      setAdjudicacion({ ...adjudicacion, cpv: updatedCPV });
    }
  };

  const handleAddDocument = () => {
    if (isEditable) {
      setAdjudicacion({
        ...adjudicacion,
        documentos: [{ link: '', nombre: '' }, ...(adjudicacion.documentos || [])],
      });
    }
  };

  const handleRemoveDocument = (index: number) => {
    if (isEditable) {
      const updatedDocuments = [...adjudicacion.documentos];
      updatedDocuments.splice(index, 1);
      setAdjudicacion({ ...adjudicacion, documentos: updatedDocuments });
    }
  };

  const formatLink = (link: string): string => {
    if (!link.startsWith('http://') && !link.startsWith('https://')) {
      return `https://${link}`;
    }
    return link;
  };

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!adjudicacion) {
    return <div className="loading">Cargando adjudicación...</div>;
  }

  // Fechas relevantes
  const now = new Date();
  const fechaPublicacion = adjudicacion.fecha_publicacion ? new Date(adjudicacion.fecha_publicacion) : null;
  const fechaFinOfertas = adjudicacion.fecha_fin_ofertas ? new Date(adjudicacion.fecha_fin_ofertas) : null;
  const fechaAdjudicacion = adjudicacion.fecha_adjudicacion ? new Date(adjudicacion.fecha_adjudicacion) : null;

  const paso1Completo = fechaPublicacion ? now >= fechaPublicacion : false;
  const paso2Completo = fechaFinOfertas ? now >= fechaFinOfertas : false;
  const paso3Completo = fechaAdjudicacion ? now >= fechaAdjudicacion : false;

  const barra1Percent = paso1Completo ? (paso2Completo ? 100 : 50) : 0;
  const barra2Percent = paso2Completo ? (paso3Completo ? 100 : 50) : 0;

  const iconoPaso1 = paso1Completo ? '/images/icons/components/tick.png' : '/images/icons/components/circle.png';
  const iconoPaso2 = paso2Completo ? '/images/icons/components/tick.png' : '/images/icons/components/circle.png';
  const iconoPaso3 = paso3Completo ? '/images/icons/components/tick.png' : '/images/icons/components/circle.png';

  // Procesamos los criterios de adjudicación
  let criterios: any[] = [];
  if (adjudicacion.criterios_adjudicacion) {
    if (Array.isArray(adjudicacion.criterios_adjudicacion)) {
      criterios = adjudicacion.criterios_adjudicacion;
    } else {
      if (adjudicacion.criterios_adjudicacion.concursoPrincipal) {
        criterios = criterios.concat(adjudicacion.criterios_adjudicacion.concursoPrincipal);
      }
      if (adjudicacion.criterios_adjudicacion.lotes) {
        adjudicacion.criterios_adjudicacion.lotes.forEach((lote: any) => {
          criterios = criterios.concat(lote.criterios);
        });
      }
    }
  }

  const criteriosData = criterios.map((criterio: any) => ({
    name: `${criterio.descripcion} (${criterio.subtipo})`,
    value: criterio.ponderacion,
  }));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AA46BE', '#6F6F6F', '#8884D8', '#82ca9d'];

  return (
    <div className="concurso-container">
      <div className="concurso-header">
        <div className="concurso-titulo">
          <h1>Adjudicación {adjudicacion.codigo_expediente}</h1>
          <p>{adjudicacion.descripcion}</p>
        </div>
        <div className="concurso-actions">
          {adjudicacion.enlace && (
            <Link to={`${adjudicacion.enlace}`}>
              <FontAwesomeIcon icon={faLink} title="Ver más" />
            </Link>
          )}
          {/* Se ha eliminado el icono del corazón */}
        </div>
      </div>

      <section className="section general-info">
        <h2>Información General de la Adjudicación</h2>
        <div className="info-grid">
          {['estado', 'importe_adjudicacion', 'duracion', 'subtipo_contrato', 'provincia'].map((field) => (
            <div className="field" key={field}>
              <label>{field.replace(/_/g, ' ').toUpperCase()}:</label>
              {editing && isEditable ? (
                <input
                  type="text"
                  value={adjudicacion[field] || ''}
                  onChange={(e) => setAdjudicacion({ ...adjudicacion, [field]: e.target.value })}
                />
              ) : (
                <span>{field === 'provincia' ? (adjudicacion.provincia || adjudicacion.localidad || 'N/A') : (adjudicacion[field] || 'N/A')}</span>
              )}
            </div>
          ))}
        </div>
      </section>

      <hr className="line" />

      <section className="project-overview progress-bar-section">
        <div className="top-dates">
          <div>
            <p className="date-title" style={{ marginBottom: '0.5rem' }}>Fecha de publicación</p>
            <p className="date-title" style={{ marginBottom: '0.5rem' }}>
              {fechaPublicacion ? fechaPublicacion.toLocaleDateString() : 'No disponible'}
            </p>
          </div>
          <div>
            <p className="date-title" style={{ marginBottom: '0.5rem', textAlign: 'right' }}>Fecha de adjudicación</p>
            <p className="date-title" style={{ marginBottom: '0.5rem', textAlign: 'right' }}>
              {fechaAdjudicacion ? fechaAdjudicacion.toLocaleDateString() : 'No disponible'}
            </p>
          </div>
        </div>

        <div className="progress-bar-with-points">
          <div className="progress-point">
            <img src={iconoPaso1} alt="Paso 1" />
          </div>

          <div className="progress-bar-segment">
            <Progress percent={barra1Percent} showInfo={false} strokeColor="#1890ff" />
          </div>

          <div className="progress-point">
            <img src={iconoPaso2} alt="Paso 2" />
          </div>

          <div className="progress-bar-segment">
            <Progress percent={barra2Percent} showInfo={false} strokeColor="#1890ff" />
          </div>

          <div className="progress-point">
            <img src={iconoPaso3} alt="Paso 3" />
          </div>
        </div>

        <div className="bottom-dates">
          <div>
            <p className="date-title" style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
              {fechaFinOfertas ? fechaFinOfertas.toLocaleString() : 'No disponible'}
            </p>
            <p className="date-title" style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
              Fecha límite de presentación
            </p>
          </div>
        </div>
      </section>

      <hr className="line" />

      <section className="section adjudicacion-section">
        <h2>Criterios y Resultados de la Adjudicación</h2>
        <div className="field adjudicacion-criterios-full">
          <label>Criterios de Adjudicación:</label>
          {criteriosData.length > 0 ? (
            <div className="criterios-container">
              <div className="criterios-pie-chart">
                <PieChart width={400} height={300}>
                  <Pie
                    data={criteriosData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {criteriosData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              </div>
              <div className="criterios-legend">
                <ul>
                  {criteriosData.map((entry, index) => (
                    <li key={`legend-${index}`}>
                      <span
                        className="legend-color"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></span>
                      <span className="legend-text">
                        {entry.name}: {entry.value}%
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <span className="no-data">No hay criterios de adjudicación disponibles</span>
          )}
        </div>

        <div className="info-grid">
          <div className="field adjudicacion-adjudicatarios">
            <label>Adjudicatarios:</label>
            {adjudicacion.adjudicatario ? (
              Array.isArray(adjudicacion.adjudicatario) ? (
                adjudicacion.adjudicatario.length > 0 ? (
                  <ul className="adjudicatarios-list">
                    {adjudicacion.adjudicatario.map((item: any, index: number) => (
                      <li key={index} className="adjudicatario-item">
                        <strong>{item.lote || 'General'}:</strong> {item.adjudicatario?.name} (
                        {item.adjudicatario?.nif || 'N/A'})
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="no-data">No adjudicatarios disponibles</span>
                )
              ) : typeof adjudicacion.adjudicatario === 'object' ? (
                adjudicacion.adjudicatario.adjudicatario ? (
                  <span>
                    {adjudicacion.adjudicatario.adjudicatario.name} (
                    {adjudicacion.adjudicatario.adjudicatario.nif || 'N/A'})
                  </span>
                ) : (
                  <span>
                    {adjudicacion.adjudicatario.name} ({adjudicacion.adjudicatario.nif || 'N/A'})
                  </span>
                )
              ) : (
                <span className="no-data">No adjudicatarios disponibles</span>
              )
            ) : (
              <span className="no-data">No adjudicatarios disponibles</span>
            )}
          </div>

          <div className="field">
            <label>Resultado del Procedimiento:</label>
            <span>{adjudicacion.resultado_adjudicacion || 'No hay resultados disponibles'}</span>
          </div>

          <div className="field">
            <label>Número de Licitadores:</label>
            <span>
              {adjudicacion.numero_licitadores ||
                'No hay licitadores para esta adjudicación'}
            </span>
          </div>

          <div className="field">
            <label>Importe de Adjudicación:</label>
            <span>
              {adjudicacion.importe_adjudicacion
                ? `${new Intl.NumberFormat('es-ES', {
                    style: 'decimal',
                    minimumFractionDigits:
                      adjudicacion.importe_adjudicacion % 1 === 0 ? 0 : 2,
                    maximumFractionDigits: 2,
                  }).format(adjudicacion.importe_adjudicacion)} €`
                : 'No hay importe de adjudicación establecido para este concurso / lote'}
            </span>
          </div>
        </div>
      </section>

      <hr className="line" />

      <section className="section cpvs-section">
        <h2>CPVs</h2>
        {editing && isEditable && (
          <button className="btn btn-add" onClick={handleAddCPV}>
            Añadir CPV
          </button>
        )}
        <ul>
          {adjudicacion.cpv?.map((cpv: string, index: number) => (
            <li key={index}>
              {editing && isEditable ? (
                <div>
                  <input
                    type="text"
                    value={cpv}
                    onChange={(e) =>
                      setAdjudicacion({
                        ...adjudicacion,
                        cpv: adjudicacion.cpv.map((c: string, i: number) =>
                          i === index ? e.target.value : c
                        ),
                      })
                    }
                  />
                  {(userRole === 'admin' || userRole === 'tecnico') && (
                    <button className="btn btn-remove" onClick={() => handleRemoveCPV(index)}>
                      Quitar
                    </button>
                  )}
                </div>
              ) : (
                <span>{cpv}</span>
              )}
            </li>
          ))}
        </ul>
      </section>

      <hr className="line" />

      <section className="section documents-section">
        <h2>Documentos</h2>
        {editing && isEditable && (
          <button className="btn btn-add" onClick={handleAddDocument}>
            Añadir Documento
          </button>
        )}
        {adjudicacion.documentos && adjudicacion.documentos.length > 0 ? (
          <ul>
            {adjudicacion.documentos.map((doc: any, index: number) => (
              <li key={index} className="document-item">
                {editing && isEditable ? (
                  <div>
                    <input
                      type="text"
                      placeholder="Nombre del documento"
                      value={doc.nombre}
                      onChange={(e) =>
                        setAdjudicacion({
                          ...adjudicacion,
                          documentos: adjudicacion.documentos.map((d: any, i: number) =>
                            i === index ? { ...d, nombre: e.target.value } : d
                          ),
                        })
                      }
                    />
                    <input
                      type="text"
                      placeholder="Enlace del documento"
                      value={doc.link}
                      onChange={(e) =>
                        setAdjudicacion({
                          ...adjudicacion,
                          documentos: adjudicacion.documentos.map((d: any, i: number) =>
                            i === index ? { ...d, link: formatLink(e.target.value) } : d
                          ),
                        })
                      }
                    />
                    {(userRole === 'admin' || userRole === 'tecnico') && (
                      <button className="btn btn-remove" onClick={() => handleRemoveDocument(index)}>
                        Quitar
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="document-info">
                    <span>{doc.nombre}</span>
                    <div className="document-buttons">
                      <a
                        className="btn btn-view-here"
                        onClick={() => handleViewPDF(doc.link, doc.nombre)}
                        title="Ver aquí"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </a>
                      <a
                        className="btn btn-view-source"
                        href={formatLink(doc.link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Ver en fuente"
                      >
                        <FontAwesomeIcon icon={faLink} />
                      </a>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>No hay documentos disponibles.</p>
        )}
      </section>

      <div className="actions">
        {editing && isEditable ? (
          <button className="btn btn-guardar" onClick={handleSave}>
            Guardar
          </button>
        ) : (
          isEditable && (
            <button className="btn btn-editar" onClick={handleEdit}>
              Editar
            </button>
          )
        )}
        {userRole === 'admin' && (
          <button className="btn btn-eliminar" onClick={handleDelete}>
            Eliminar
          </button>
        )}
      </div>

      {pdfViewer.isOpen && (
        <div className="pdf-viewer-overlay">
          <div className="pdf-viewer">
            <div className="pdf-viewer-header">
              <span className="pdf-title">{pdfViewer.name}</span>
              <button className="btn btn-close" onClick={closePDFViewer}>
                X
              </button>
            </div>
            <iframe src={pdfViewer.link} title="Visor PDF" width="100%" height="600px"></iframe>
          </div>
        </div>
      )}
    </div>
  );
};

export default Adjudicacion;
